import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Alert, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { downloadCsv } from '../../utils/csv';
import { formatDateTime } from '../../utils/dateUtils';
import {
    DataImportService, ImportJobResult, ImportJobSummary, ImportKind, ImportTemplate,
} from '../../services/integration.service';

interface KindOption { id: ImportKind; label: string; key: string; }
/** Row notes of the cash transactions import (TransactionImportHandler): predicted at the check, actual after the commit. */
const WILL_HOLD = 'will be held for approval';
const HELD = 'held for approval';
const KINDS: KindOption[] = [
    { id: 'CUSTOMERS', label: 'Customers', key: PERMISSIONS.CUSTOMER_MANAGE },
    { id: 'SUPPLIERS', label: 'Suppliers (with optional bank details)', key: PERMISSIONS.SUPPLIER_MANAGE },
    { id: 'ITEMS', label: 'Items', key: PERMISSIONS.ITEM_MANAGE },
    { id: 'OPENING_AR', label: 'Opening receivables (customer open invoices)', key: PERMISSIONS.ACCOUNT_MANAGE },
    { id: 'OPENING_AP', label: 'Opening payables (supplier open bills)', key: PERMISSIONS.ACCOUNT_MANAGE },
    // F11 (11a): blank category = Auto (mapping rule); rows over the approval threshold are held, not posted
    { id: 'TRANSACTIONS', label: 'Cash transactions (in / out)', key: PERMISSIONS.TRANSACTION_MANAGE },
];

/**
 * Master-data and opening-balance import (CSV or XLSX): pick a kind, download the template, choose a file to check (dry-run, no
 * writes, row errors listed with their row number), then commit. A committed file cannot be committed twice.
 */
const DataImport: React.FC = () => {
    const toast = useToast();
    const [searchParams] = useSearchParams();   // FW-1: /data-import?kind=CUSTOMERS|SUPPLIERS|... opens that kind
    const canCustomers = usePermission(PERMISSIONS.CUSTOMER_MANAGE);
    const canSuppliers = usePermission(PERMISSIONS.SUPPLIER_MANAGE);
    const canItems = usePermission(PERMISSIONS.ITEM_MANAGE);
    const canAccounts = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canTransactions = usePermission(PERMISSIONS.TRANSACTION_MANAGE);   // F11 (11a)
    const allowed = useMemo(() => KINDS.filter(k =>
        (k.key === PERMISSIONS.CUSTOMER_MANAGE && canCustomers) || (k.key === PERMISSIONS.SUPPLIER_MANAGE && canSuppliers)
        || (k.key === PERMISSIONS.ITEM_MANAGE && canItems) || (k.key === PERMISSIONS.ACCOUNT_MANAGE && canAccounts)
        || (k.key === PERMISSIONS.TRANSACTION_MANAGE && canTransactions)),
    [canCustomers, canSuppliers, canItems, canAccounts, canTransactions]);

    const fileInput = useRef<HTMLInputElement>(null);
    const [kind, setKind] = useState<ImportKind | ''>('');
    const [template, setTemplate] = useState<ImportTemplate | null>(null);
    const [goLiveDate, setGoLiveDate] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [report, setReport] = useState<ImportJobResult | null>(null);
    const [history, setHistory] = useState<ImportJobSummary[]>([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!kind && allowed.length > 0) {
            const requested = (searchParams.get('kind') ?? '').toUpperCase();
            setKind((allowed.find(k => k.id === requested) ?? allowed[0]).id);
        }
    }, [allowed, kind, searchParams]);

    const loadKind = useCallback(async (k: ImportKind) => {
        try {
            const [t, h] = await Promise.all([DataImportService.template(k), DataImportService.history(k)]);
            setTemplate(t);
            setHistory(h);
        } catch (e) {
            toast.showApiError(e, 'Failed to load the import template');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** A dry-run is recorded as a job too, so the history is re-read after every check and every commit. */
    const refreshHistory = useCallback(async (k: ImportKind) => {
        try {
            setHistory(await DataImportService.history(k));
        } catch (e) {
            toast.showApiError(e, 'Failed to refresh the import history');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setReport(null);
        setFile(null);
        setTemplate(null);
        if (kind) loadKind(kind);
    }, [kind, loadKind]);

    const needsDate = !!template?.needsGoLiveDate;
    const dateMissing = needsDate && !goLiveDate;

    const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0] ?? null;
        e.target.value = '';
        setReport(null);
        setFile(picked);
        if (!picked || !kind) return;
        if (dateMissing) { toast.showError('Enter the go-live date first'); return; }
        setBusy(true);
        try {
            setReport(await DataImportService.dryRun(kind, picked, goLiveDate || undefined));
            await refreshHistory(kind);
        } catch (err) {
            toast.showApiError(err, 'Dry-run failed');
        } finally {
            setBusy(false);
        }
    };

    const canCommit = !!report && report.status === 'DRY_RUN' && report.batchErrors.length === 0 && report.errorRows === 0
        && report.validRows > 0;

    const commit = async () => {
        if (!file || !kind || !canCommit || busy) return;
        setBusy(true);
        try {
            const res = await DataImportService.commit(kind, file, goLiveDate || undefined);
            if (res.status === 'COMMITTED') {
                toast.showSuccess(`Imported ${res.createdCount} records`);
                setFile(null);
                setReport(res);   // polish P2-1: the result (held rows, rule notes) stays on screen after the commit
                await refreshHistory(kind);
            } else {
                setReport(res);
                toast.showError('The file has errors; nothing was imported');
                await refreshHistory(kind);
            }
        } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 409) toast.showError('This file was already imported. Nothing was created.');
            else toast.showApiError(err, 'Import failed');
        } finally {
            setBusy(false);
        }
    };

    // Polish P2-1: OK rows that carry a note (rule applied, will be / was held for approval) are listed too.
    const attention = (report?.rows ?? []).filter(r => r.status !== 'OK' || r.messages.length > 0);
    const committed = report?.status === 'COMMITTED';
    const heldCount = (report?.rows ?? []).filter(r => r.messages.some(m => m === HELD || m === WILL_HOLD)).length;

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="data.import.page">
            <Typography variant="h5" gutterBottom>Data Import</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Load customers, suppliers, items, opening balances and cash transactions (in / out) from a CSV or XLSX file (up to 2 MB and 5,000 rows). Every file is
                checked first with nothing written; the import runs only when every row is valid.
            </Typography>
            {allowed.length === 0 && <Alert severity="info">You do not have permission to import any data kind.</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <Box sx={{ flex: '1 1 280px', maxWidth: 420 }} data-help="data.import.kind">
                    <SearchableSelect options={allowed} value={kind} onChange={id => setKind((id as ImportKind) || '')}
                        getOptionLabel={o => o.label} label="What to import" size="small" />
                </Box>
                {needsDate && (
                    <TextField size="small" type="date" label="Go-live date" data-help="data.import.field.goLive" value={goLiveDate} InputLabelProps={{ shrink: true }}
                        onChange={e => { setGoLiveDate(e.target.value); setReport(null); setFile(null); }} sx={{ flex: '1 1 160px', maxWidth: 220 }} />
                )}
            </Box>

            {template && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Columns: {template.columns.join(', ')}. Required: {template.requiredColumns.join(', ')}. {template.notes}
                    </Alert>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <Button data-help="data.import.action.template" variant="outlined" startIcon={<FileDownloadIcon />}
                            onClick={() => downloadCsv(`${template.kind.toLowerCase()}-import-template.csv`, template.columns, template.sampleRows)}>
                            Download template
                        </Button>
                        <input ref={fileInput} data-testid="data-import-file" type="file" accept=".csv,.xlsx" hidden onChange={onPick} />
                        <Button data-help="data.import.action.check" {...helpWhy(!busy && dateMissing ? { kind: 'status', message: 'Enter the go-live date first.' } : null)} variant="contained" startIcon={<CloudUploadIcon />} disabled={busy || dateMissing}
                            onClick={() => fileInput.current?.click()}>
                            Choose file and check
                        </Button>
                        <Button data-help="data.import.action.import" {...helpWhy(!busy && !canCommit ? { kind: 'status', message: 'Choose a file and check it first. Import opens when every row is valid.' } : null)} variant="contained" color="success" disabled={!canCommit || busy} onClick={commit}>
                            Import
                        </Button>
                    </Box>
                </>
            )}

            {report && (
                <Paper sx={{ p: 2, mb: 3 }} data-testid="data-import-report" data-help-section="data.import.report">
                    <Typography variant="subtitle1" gutterBottom>
                        {file?.name ?? report.fileName}: {report.totalRows} rows, {report.validRows} valid, {report.errorRows} with errors
                        {report.skippedRows > 0 ? `, ${report.skippedRows} skipped` : ''}
                    </Typography>
                    {committed && (
                        <Alert severity="success" sx={{ mb: 1 }} data-testid="data-import-committed">
                            Imported {report.createdCount} records{heldCount > 0 ? `; ${heldCount} held for approval (approve them in Approvals, then post them from Transactions)` : ''}.
                        </Alert>
                    )}
                    {!committed && heldCount > 0 && (
                        <Alert severity="info" sx={{ mb: 1 }} data-testid="data-import-will-hold">
                            {heldCount} {heldCount === 1 ? 'row matches' : 'rows match'} an approval workflow and will be held for approval instead of posting.
                        </Alert>
                    )}
                    {report.batchErrors.map(e => <Alert key={e} severity="error" sx={{ mb: 1 }}>{e}</Alert>)}
                    {attention.length > 0 && (
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow><TableCell data-help="data.import.col.row">Row</TableCell><TableCell data-help="data.import.col.record">Record</TableCell><TableCell data-help="data.import.col.rstatus">Status</TableCell><TableCell data-help="data.import.col.notes">Notes</TableCell></TableRow>
                                </TableHead>
                                <TableBody>
                                    {attention.map(r => (
                                        <TableRow key={r.rowNo}>
                                            <TableCell>{r.rowNo}</TableCell>
                                            <TableCell>{r.key}</TableCell>
                                            <TableCell><Chip size="small" label={r.status} color={r.status === 'ERROR' ? 'error' : r.status === 'OK' ? 'info' : 'default'} variant={r.status === 'OK' ? 'outlined' : 'filled'} /></TableCell>
                                            <TableCell>{r.messages.join('; ')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {!committed && report.errorRows === 0 && report.batchErrors.length === 0 && (
                        <Alert severity="success">Every row is valid. Press Import to create the records.</Alert>
                    )}
                </Paper>
            )}

            <Typography variant="h6" gutterBottom>Recent imports</Typography>
            <TableContainer component={Paper}>
                <Table size="small" data-help-section="data.import.history">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="data.import.col.when">When</TableCell><TableCell data-help="data.import.col.file">File</TableCell><TableCell data-help="data.import.col.hstatus">Status</TableCell>
                            <TableCell data-help="data.import.col.rows" align="right">Rows</TableCell><TableCell data-help="data.import.col.errors" align="right">Errors</TableCell><TableCell data-help="data.import.col.created" align="right">Created</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.length === 0 && <TableRow><TableCell colSpan={6}>No imports yet.</TableCell></TableRow>}
                        {history.map(h => (
                            <TableRow key={h.id}>
                                <TableCell>{h.createdAt ? formatDateTime(h.createdAt) : ''}</TableCell>
                                <TableCell>{h.fileName}</TableCell>
                                <TableCell>{h.status}</TableCell>
                                <TableCell align="right">{h.totalRows}</TableCell>
                                <TableCell align="right">{h.errorRows}</TableCell>
                                <TableCell align="right">{h.createdCount}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default DataImport;
